import React, { useState, useEffect } from 'react';
import { UserPlus, Edit2, Trash2, X, Eye, EyeOff, Shield, Check, Download, Clock, Dumbbell, Tag, Plus, Activity } from 'lucide-react';
import { ALL_TABS, HOME_ONLY_TABS, PILLAR_BUBBLE_TABS } from '../tabs';
import {
  getUsers, createUser, updateUser, deleteUser, syncStaging, changePassword, getUserPassword,
  getTestInputAccess, grantTestInputAccess, revokeTestInputAccess,
  getPassingDrillCalibration, updatePassingDrillCalibration,
  getPlayers, getPlayerAliases, createPlayerAlias, deletePlayerAlias,
  sendRPEReminder,
  sendWellnessReminder, getWellnessReminderSettings, updateWellnessReminderSettings,
} from '../api';
import { useTheme } from '../ThemeContext';
import { useSeason, seasonLabel } from '../SeasonContext';
import NotificationsPanel from '../NotificationsPanel';
import './Settings.css';

// 'testinput' access is granted separately via the Physical Testing input-access list, not the
// general tab checklist. HOME_ONLY_TABS (Team Table, Trainer Ratings, Squad Builder) and
// PILLAR_BUBBLE_TABS (Tactical/Identity/Technical Overview — 2026-07-29) are merged in here so
// admins can actually grant/revoke them; they're deliberately left out of ALL_TABS itself so
// they don't also reappear as duplicate buttons in the old top nav bar.
const TAB_OPTIONS = [...ALL_TABS, ...HOME_ONLY_TABS, ...PILLAR_BUBBLE_TABS].filter(t => t.id !== 'testinput').map(t => ({ id: t.id, label: t.label }));
// 'home' is deliberately excluded from "every tab, pre-checked" — confirmed 2026-07-29: new
// (and by extension most existing, since none had a checkbox for it before now) accounts
// should land on Team Overview instead, not the full Home dashboard. Admins still get it
// automatically regardless of this list (isAdmin bypasses allowedTabs everywhere, same as
// every other tab) — this only affects the non-admin default.
// 'einstein' excluded the same way (2026-09-10, explicit request): still being tested, so new
// non-admin accounts should NOT get it automatically — an admin grants it per account here as
// trust in it grows, same mechanism as every other tab, just defaulted off instead of on.
// 'planner-edit' (2026-10-01) likewise: edit rights on the Club Planner are granted per account.
const DEFAULT_NEW_USER_TABS = TAB_OPTIONS.map(t => t.id).filter(id => id !== 'home' && id !== 'einstein' && id !== 'planner-edit');

const GPS_SUB_GROUPS = [
  { section: 'Team',   tabs: [
    { id: 'team-training',      label: 'Team Training' },
    { id: 'ewma',               label: 'EWMA' },
    { id: 'team-week',          label: 'Team Week' },
    { id: 'week-summary',       label: 'Week Summary' },
    { id: 'trends',             label: 'Trends' },
  ]},
  { section: 'Player', tabs: [
    { id: 'player-chronic',     label: 'Player Chronic' },
    { id: 'max-speed',          label: 'Max Speed' },
    { id: 'individual-targets', label: 'Individual Targets' },
    { id: 'future-targets',     label: 'Future Targets' },
  ]},
  { section: 'Match',  tabs: [
    { id: 'match-performance',  label: 'Match Performance' },
    { id: 'in-match-analysis',  label: 'In-Match Analysis' },
    { id: 'match-chronic',      label: 'Match Chronic' },
  ]},
  { section: 'Goalkeeper', tabs: [
    { id: 'goalkeeper',        label: 'Goalkeeper' },
  ]},
  { section: 'Rehab', tabs: [
    { id: 'rehab-training',    label: 'Training overview' },
  ]},
];
const ALL_GPS_SUB_IDS = GPS_SUB_GROUPS.flatMap(g => g.tabs.map(t => t.id));

// ── Create / Edit modal ────────────────────────────────────────────────────────
const ROLES = [
  { id: 'staff',  label: 'Staff' },
  { id: 'physio', label: 'Physio' },
  { id: 'player', label: 'Player' },
  { id: 'admin',  label: 'Admin' },
];

function UserModal({ editing, currentUser, onSave, onClose }) {
  const [name, setName]         = useState(editing?.name || '');
  const [username, setUsername] = useState(editing?.username || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [role, setRole]         = useState(
    editing?.role || (editing?.isAdmin ? 'admin' : 'staff')
  );
  const [allowed, setAllowed]   = useState(
    editing?.allowedTabs || DEFAULT_NEW_USER_TABS
  );
  const [allowedGpsTabs, setAllowedGpsTabs] = useState(
    editing?.allowedGpsTabs ?? ALL_GPS_SUB_IDS
  );
  const [error, setError]  = useState('');
  const [saving, setSaving] = useState(false);

  const toggleTab = id =>
    setAllowed(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  const toggleGpsTab = id =>
    setAllowedGpsTabs(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  const isSelf  = editing?.id === currentUser.id;
  const isAdmin = role === 'admin';

  const handleSubmit = async e => {
    e.preventDefault();
    if (!name.trim())     return setError('Display name is required.');
    if (!username.trim()) return setError('Username is required.');
    if (!editing && !password) return setError('Password is required for new accounts.');

    const gpsEnabled = role === 'admin' || allowed.includes('gps');
    // physio always gets medical + availability tab access, and Rehab GPS access
    const physioExtraTabs = ['medical', 'availability'].filter(t => !allowed.includes(t));
    const effectiveTabs = role === 'physio' ? [...allowed, ...physioExtraTabs] : allowed;
    const physioExtraGpsTabs = ['rehab-training'].filter(t => !allowedGpsTabs.includes(t));
    const effectiveGpsTabs = role === 'physio' ? [...allowedGpsTabs, ...physioExtraGpsTabs] : allowedGpsTabs;
    const data = {
      name:            name.trim(),
      username:        username.trim(),
      role,
      isAdmin:         role === 'admin',
      allowedTabs:     role === 'admin' ? TAB_OPTIONS.map(t => t.id) : effectiveTabs,
      allowedGpsTabs:  role === 'admin' ? null : (gpsEnabled ? effectiveGpsTabs : null),
    };
    if (password) data.password = password;

    setSaving(true);
    try {
      await onSave(data);
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="sm-overlay">
      <div className="sm-modal">
        <div className="sm-modal__header">
          <h3>{editing ? 'Edit Account' : 'Create Account'}</h3>
          <button className="sm-close" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="sm-form">
          <div className="sm-field">
            <label>Display Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
          </div>

          <div className="sm-field">
            <label>Username</label>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Login username"
              disabled={isSelf}
            />
          </div>

          <div className="sm-field">
            <label>{editing ? 'New Password' : 'Password'}</label>
            <div className="sm-pw-wrap">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={editing ? 'Leave blank to keep current' : 'Set password'}
              />
              <button type="button" className="sm-pw-eye" onClick={() => setShowPw(p => !p)}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="sm-field">
            <label>Role</label>
            <div className="sm-role-selector">
              {ROLES.map(r => (
                <button
                  key={r.id}
                  type="button"
                  className={`sm-role-chip sm-role-chip--${r.id} ${role === r.id ? 'sm-role-chip--active' : ''}`}
                  onClick={() => {
                    if (isSelf && r.id !== 'admin') return;
                    setRole(r.id);
                  }}
                  title={isSelf && r.id !== 'admin' ? "Can't remove your own admin role" : ''}
                >
                  {r.id === 'admin' && <Shield size={12} />}
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {isAdmin ? (
            <div className="sm-admin-notice">
              <Shield size={13} /> Admins automatically have access to all tabs.
            </div>
          ) : (
            <>
              <div className="sm-field">
                <label>Tab Access</label>
                <div className="sm-tabs-grid">
                  {TAB_OPTIONS.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`sm-tab-chip ${allowed.includes(tab.id) ? 'sm-tab-chip--on' : ''}`}
                      onClick={() => toggleTab(tab.id)}
                    >
                      {allowed.includes(tab.id) && <Check size={11} />}
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {allowed.includes('gps') && (
                <div className="sm-field">
                  <label>GPS Dashboard Access</label>
                  <div className="sm-gps-sub-section">
                    {GPS_SUB_GROUPS.map((group, gi) => (
                      <div key={group.section} className="sm-gps-group">
                        {gi > 0 && <div className="sm-gps-divider" />}
                        <div className="sm-gps-group__label">{group.section}</div>
                        <div className="sm-gps-group__chips">
                          {group.tabs.map(tab => (
                            <button
                              key={tab.id}
                              type="button"
                              className={`sm-tab-chip ${allowedGpsTabs.includes(tab.id) ? 'sm-tab-chip--on' : ''}`}
                              onClick={() => toggleGpsTab(tab.id)}
                            >
                              {allowedGpsTabs.includes(tab.id) && <Check size={11} />}
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {error && <div className="sm-error">{error}</div>}

          <div className="sm-modal__actions">
            <button type="button" className="sm-btn sm-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="sm-btn sm-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Admin password cell ───────────────────────────────────────────────────────
function PasswordCell({ userId }) {
  const [visible,   setVisible]   = useState(false);
  const [password,  setPassword]  = useState(null);
  const [changedAt, setChangedAt] = useState(null);
  const [loading,   setLoading]   = useState(false);

  const reveal = async () => {
    if (password !== null) { setVisible(v => !v); return; }
    setLoading(true);
    try {
      const data = await getUserPassword(userId);
      setPassword(data.password || '');
      setChangedAt(data.password_changed_at);
      setVisible(true);
    } catch { setPassword('(error)'); setVisible(true); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-primary)', minWidth: 120 }}>
        {visible ? password : '••••••••'}
      </span>
      <button onClick={reveal} disabled={loading} title={visible ? 'Hide password' : 'Show password'}
        style={{ background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', alignItems: 'center' }}>
        {loading ? '…' : visible ? <EyeOff size={14}/> : <Eye size={14}/>}
      </button>
      {changedAt && (
        <span title={`Changed: ${new Date(changedAt).toLocaleString()}`} style={{ color: 'var(--text-dimmed)', display: 'flex', alignItems: 'center' }}>
          <Clock size={11}/>
        </span>
      )}
    </div>
  );
}

// ── Change password panel ─────────────────────────────────────────────────────
function ChangePasswordPanel({ user }) {
  const [cur,  setCur]  = useState('');
  const [nw,   setNw]   = useState('');
  const [conf, setConf] = useState('');
  const [show, setShow] = useState(false);
  const [msg,  setMsg]  = useState(null); // { ok, text }
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    if (!cur || !nw || !conf) return setMsg({ ok: false, text: 'All fields are required.' });
    if (nw !== conf)          return setMsg({ ok: false, text: 'New passwords do not match.' });
    if (nw.length < 8)        return setMsg({ ok: false, text: 'Password must be at least 8 characters.' });
    setBusy(true); setMsg(null);
    try {
      await changePassword(user.id, cur, nw);
      setMsg({ ok: true, text: 'Password updated successfully.' });
      setCur(''); setNw(''); setConf('');
    } catch (err) {
      const txt = err.message?.includes('401') || err.message?.includes('incorrect')
        ? 'Current password is incorrect.'
        : 'Failed to update password.';
      setMsg({ ok: false, text: txt });
    } finally { setBusy(false); }
  };

  const inp = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Change Password</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>Update your login password.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}>
        <div style={{ position: 'relative' }}>
          <input type={show ? 'text' : 'password'} placeholder="Current password" value={cur} onChange={e => setCur(e.target.value)} style={inp} />
          <button type="button" onClick={() => setShow(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            {show ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        </div>
        <input type={show ? 'text' : 'password'} placeholder="New password (min 8 chars)" value={nw} onChange={e => setNw(e.target.value)} style={inp} />
        <input type={show ? 'text' : 'password'} placeholder="Confirm new password" value={conf} onChange={e => setConf(e.target.value)} style={inp} />
        {msg && <div style={{ fontSize: 13, color: msg.ok ? '#10b981' : '#ef4444', fontWeight: 600 }}>{msg.text}</div>}
        <button onClick={handle} disabled={busy} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: busy ? 'var(--border)' : '#2563eb', color: busy ? 'var(--text-dimmed)' : '#fff', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', alignSelf: 'flex-start' }}>
          {busy ? 'Updating…' : 'Update Password'}
        </button>
      </div>
    </div>
  );
}

// ── Season filter panel ───────────────────────────────────────────────────────
function SeasonPanel() {
  const { selectedSeasons, setSelectedSeasons, availableSeasons } = useSeason();
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Season Filter</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Choose which season's data to display across all GPS views.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {availableSeasons.map(s => {
          const active = selectedSeasons.includes(s);
          return (
            <button key={s} onClick={() => setSelectedSeasons([s])} style={{
              padding: '8px 18px', borderRadius: 99,
              border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
              background: active ? '#3b82f6' : 'var(--bg-app)',
              color: active ? '#fff' : 'var(--text-secondary)',
              fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {seasonLabel(s)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── PWA install panel ─────────────────────────────────────────────────────────
function InstallPanel() {
  const [promptReady, setPromptReady] = useState(!!window.deferredInstallPrompt);

  useEffect(() => {
    if (window.deferredInstallPrompt) setPromptReady(true);
    const handler = () => setPromptReady(true);
    const onInstalled = () => { setPromptReady(false); window.deferredInstallPrompt = null; };
    window.addEventListener('installpromptready', handler);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('installpromptready', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!window.deferredInstallPrompt) return;
    window.deferredInstallPrompt.prompt();
    const { outcome } = await window.deferredInstallPrompt.userChoice;
    if (outcome === 'accepted') {
      window.deferredInstallPrompt = null;
      setPromptReady(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Install App</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Install HeraLabX as an app for quick access from your home screen or desktop.
      </p>
      <button
        onClick={handleInstall}
        disabled={!promptReady}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: 'none', background: promptReady ? '#2563eb' : 'var(--border)', color: promptReady ? '#fff' : 'var(--text-dimmed)', cursor: promptReady ? 'pointer' : 'default', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}
      >
        <Download size={15} />
        Install HeraLabX
      </button>
    </div>
  );
}

// ── Theme toggle panel ─────────────────────────────────────────────────────────
function ThemePanel() {
  const { theme, setTheme } = useTheme();
  const isDay = theme === 'day';

  const optBtn = (label, val, icon) => ({
    padding: '10px 20px',
    borderRadius: 10,
    border: `1px solid ${theme === val ? '#3b82f6' : 'var(--border-strong)'}`,
    background: theme === val ? '#3b82f6' : 'var(--bg-app)',
    color: theme === val ? '#fff' : 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  });

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Appearance</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>Choose how the dashboard looks.</p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button style={optBtn('Night', 'night')} onClick={() => setTheme('night')}>
          <span>🌙</span> Night
        </button>
        <button style={optBtn('Day', 'day')} onClick={() => setTheme('day')}>
          <span>☀️</span> Day
        </button>
      </div>
    </div>
  );
}

// ── Physical Testing admin panel ──────────────────────────────────────────────
function PhysicalTestingPanel({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [calib, setCalib] = useState(null);
  const [calibForm, setCalibForm] = useState(null);
  const [calibSaving, setCalibSaving] = useState(false);
  const [calibMsg, setCalibMsg] = useState(null);

  const loadAccounts = () => getTestInputAccess().then(setAccounts).catch(() => {}).finally(() => setLoadingAccounts(false));

  useEffect(() => {
    loadAccounts();
    getPassingDrillCalibration().then(c => { setCalib(c); setCalibForm(c); }).catch(() => {});
  }, []);

  const toggleAccess = async acc => {
    setAccounts(prev => prev.map(a => a.id === acc.id ? { ...a, granted_at: acc.granted_at ? null : new Date().toISOString() } : a));
    try {
      if (acc.granted_at) await revokeTestInputAccess(acc.id);
      else await grantTestInputAccess({ account_id: acc.id, granted_by: user.id });
      await loadAccounts();
    } catch {
      await loadAccounts();
    }
  };

  const handleCalibSave = async () => {
    setCalibSaving(true);
    setCalibMsg(null);
    try {
      const saved = await updatePassingDrillCalibration({
        coef_td: Number(calibForm.coef_td),
        coef_hsrd: Number(calibForm.coef_hsrd),
        reference_td: Number(calibForm.reference_td),
        reference_hsrd: Number(calibForm.reference_hsrd),
        updated_by: user.id,
      });
      setCalib(saved);
      setCalibForm(saved);
      setCalibMsg({ ok: true, text: 'Calibration saved.' });
    } catch {
      setCalibMsg({ ok: false, text: 'Failed to save calibration.' });
    } finally {
      setCalibSaving(false);
    }
  };

  const calibInput = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Dumbbell size={16} color="var(--text-primary)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Physical Testing</span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Manage who can enter physical test results, and the Passing Drill HR Test calibration.
      </p>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10 }}>Test Input Access</div>
        {loadingAccounts ? (
          <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {accounts.map(acc => {
              const granted = !!acc.granted_at;
              return (
                <div key={acc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: 'var(--bg-app)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{acc.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>@{acc.username}</div>
                  </div>
                  <button onClick={() => toggleAccess(acc)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 99, border: `1px solid ${granted ? '#10b981' : 'var(--border-strong)'}`, background: granted ? '#10b98122' : 'var(--bg-card)', color: granted ? '#10b981' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {granted && <Check size={12} />} {granted ? 'Granted' : 'Grant access'}
                  </button>
                </div>
              );
            })}
            {accounts.length === 0 && <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>No staff/physio accounts found.</div>}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10 }}>Passing Drill Calibration</div>
        {!calibForm ? (
          <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 420 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Coefficient TD</label>
                <input type="number" value={calibForm.coef_td} onChange={e => setCalibForm(f => ({ ...f, coef_td: e.target.value }))} style={calibInput} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Coefficient HSRD</label>
                <input type="number" value={calibForm.coef_hsrd} onChange={e => setCalibForm(f => ({ ...f, coef_hsrd: e.target.value }))} style={calibInput} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Reference TD</label>
                <input type="number" value={calibForm.reference_td} onChange={e => setCalibForm(f => ({ ...f, reference_td: e.target.value }))} style={calibInput} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Reference HSRD</label>
                <input type="number" value={calibForm.reference_hsrd} onChange={e => setCalibForm(f => ({ ...f, reference_hsrd: e.target.value }))} style={calibInput} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, margin: '12px 0 4px' }}>
              Provisional calibration — first-pass fit, refit recommended as more sessions accumulate.
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-dimmed)', margin: '0 0 8px', maxWidth: 480 }}>
              Last calibrated: {calib?.updated_at ? new Date(calib.updated_at).toLocaleDateString() : '—'}, n=19 players / 82 sessions
              (mean 4.3 sessions/player, range 2-5). Fit from our own paired passing-drill + ISRT MAX data via a
              mixed-effects model (%HRmax_passing ~ TD + HSRD + (1|player)). TD coefficient is borderline (p=0.099);
              HSRD is significant (p=0.035). A validity spot-check against ISRT MAX submaximal HR (n=16) showed the
              adjustment roughly doubling correlation strength vs. the raw number (r=0.21 → r=0.45) — directionally
              promising but not yet statistically robust at this sample size.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={handleCalibSave} disabled={calibSaving}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: calibSaving ? 'var(--border)' : '#2563eb', color: calibSaving ? 'var(--text-dimmed)' : '#fff', fontSize: 13, fontWeight: 600, cursor: calibSaving ? 'default' : 'pointer' }}>
                {calibSaving ? 'Saving…' : 'Save calibration'}
              </button>
              {calibMsg && <span style={{ fontSize: 12, color: calibMsg.ok ? '#10b981' : '#ef4444', fontWeight: 600 }}>{calibMsg.text}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Player Name Aliases panel ───────────────────────────────────────────────────
function PlayerAliasesPanel({ user }) {
  const [aliases, setAliases] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [newPlayerId, setNewPlayerId] = useState('');
  const [newAliasName, setNewAliasName] = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = () => Promise.all([getPlayerAliases(), getPlayers()])
    .then(([a, p]) => { setAliases(a || []); setPlayers((p || []).filter(pl => pl.active !== false)); })
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newPlayerId || !newAliasName.trim()) { setError('Pick a player and enter the alias name.'); return; }
    setSaving(true);
    setError('');
    try {
      await createPlayerAlias({ player_id: newPlayerId, alias_name: newAliasName.trim(), created_by: user.id });
      setNewPlayerId('');
      setNewAliasName('');
      setAdding(false);
      await load();
    } catch {
      setError('Failed to add alias — it may already be in use.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setAliases(prev => prev.filter(a => a.id !== id));
    try { await deletePlayerAlias(id); } catch { await load(); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Tag size={16} color="var(--text-primary)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Player Name Aliases</span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        If a GPS export (e.g. Statsports) misspells a player's name, add an alias here so future uploads still map to the right player — no code change needed.
      </p>

      {loading ? (
        <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {aliases.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: 'var(--bg-app)' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {a.alias_name} <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>→</span> {a.player_name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Added {new Date(a.created_at).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                </div>
                <button onClick={() => handleDelete(a.id)} title="Delete alias"
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {aliases.length === 0 && <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>No aliases configured yet.</div>}
          </div>

          {adding ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 420 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Maps to player</label>
                <select style={inputStyle} value={newPlayerId} onChange={e => setNewPlayerId(e.target.value)}>
                  <option value="">— select player —</option>
                  {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Alias / misspelled name (as it appears in the export)</label>
                <input style={inputStyle} value={newAliasName} onChange={e => setNewAliasName(e.target.value)} placeholder="e.g. Tristan van Gils" />
              </div>
              {error && <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setAdding(false); setError(''); }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={handleAdd} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: saving ? 'var(--border)' : '#2563eb', color: saving ? 'var(--text-dimmed)' : '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Save alias'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <Plus size={14} /> Add alias
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── RPE reminder panel ─────────────────────────────────────────────────────────
function RPEReminderPanel() {
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await sendRPEReminder();
      setResult({ ok: true, msg: `Sent to ${r.sent} player${r.sent === 1 ? '' : 's'} · ${r.skipped} already submitted or no device registered.` });
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Failed to send.' });
    } finally { setSending(false); }
  };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>RPE Reminder</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Send a push notification to every player who hasn't submitted their RPE today. Only reaches players with notifications enabled.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={handleSend} disabled={sending}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: sending ? 'var(--border)' : '#3b82f6', color: sending ? 'var(--text-dimmed)' : '#fff', cursor: sending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
          {sending ? 'Sending…' : 'Send RPE reminder now'}
        </button>
        {result && (
          <span style={{ fontSize: 12, color: result.ok ? '#6ee7b7' : '#f87171' }}>{result.msg}</span>
        )}
      </div>
    </div>
  );
}

// ── Wellness reminder panel ────────────────────────────────────────────────────
function WellnessReminderPanel() {
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);

  const [settings, setSettings]   = useState(null); // { id, reminder_time, title, body }
  const [draft, setDraft]         = useState({ reminder_time: '08:00', title: '', body: '' });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);

  useEffect(() => {
    getWellnessReminderSettings()
      .then(s => {
        if (s) {
          setSettings(s);
          setDraft({ reminder_time: s.reminder_time, title: s.title, body: s.body });
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, []);

  const dirty = settings && (
    draft.reminder_time !== settings.reminder_time ||
    draft.title !== settings.title ||
    draft.body !== settings.body
  );

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await sendWellnessReminder();
      setResult({ ok: true, msg: `Sent to ${r.sent} player${r.sent === 1 ? '' : 's'} · ${r.skipped} already submitted or no device registered.` });
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Failed to send.' });
    } finally { setSending(false); }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const updated = await updateWellnessReminderSettings(draft);
      setSettings(updated);
      setDraft({ reminder_time: updated.reminder_time, title: updated.title, body: updated.body });
      setSaveMsg({ ok: true, msg: 'Saved.' });
    } catch (err) {
      setSaveMsg({ ok: false, msg: err.message || 'Failed to save.' });
    } finally { setSaving(false); }
  };

  const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block' };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Wellness Reminder</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Automatically sent every day (Europe/Amsterdam time) to every player who hasn't submitted their wellness questionnaire yet. Only reaches players with notifications enabled.
      </p>

      {!loadingSettings && settings && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Time (24h)</label>
              <input type="time" value={draft.reminder_time} onChange={e => setDraft(d => ({ ...d, reminder_time: e.target.value }))} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Notification title</label>
              <input type="text" value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} style={fieldStyle} maxLength={100} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Notification text</label>
            <textarea value={draft.body} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
              style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <button onClick={handleSaveSettings} disabled={!dirty || saving}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: (!dirty || saving) ? 'var(--border)' : '#3b82f6', color: (!dirty || saving) ? 'var(--text-dimmed)' : '#fff', cursor: (!dirty || saving) ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {saveMsg && <span style={{ fontSize: 12, color: saveMsg.ok ? '#6ee7b7' : '#f87171' }}>{saveMsg.msg}</span>}
          </div>
        </>
      )}

      <div style={{ height: 1, background: 'var(--border)', margin: '0 0 18px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={handleSend} disabled={sending}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: sending ? 'var(--border)' : '#3b82f6', color: sending ? 'var(--text-dimmed)' : '#fff', cursor: sending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
          {sending ? 'Sending…' : 'Send wellness reminder now'}
        </button>
        {result && (
          <span style={{ fontSize: 12, color: result.ok ? '#6ee7b7' : '#f87171' }}>{result.msg}</span>
        )}
      </div>
    </div>
  );
}

// ── Staging sync panel ─────────────────────────────────────────────────────────
function StagingPanel() {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult]   = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      await syncStaging();
      setResult({ ok: true, msg: 'Staging DB synced successfully.' });
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ marginTop: 32, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Staging Environment</span>
        <span style={{ fontSize: 11, background: 'var(--border)', color: 'var(--text-muted)', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>Admin only</span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Overwrites all staging data with a fresh copy of production. Safe to run anytime.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: syncing ? 'var(--border)' : '#2563eb', color: syncing ? 'var(--text-dimmed)' : '#fff', cursor: syncing ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          {syncing ? 'Syncing…' : 'Sync production → staging DB'}
        </button>
        {result && (
          <span style={{ fontSize: 12, color: result.ok ? '#6ee7b7' : '#f87171' }}>{result.msg}</span>
        )}
      </div>
    </div>
  );
}

// Admin-only entry point into the Usage Analytics page (2026-08-23) — a panel here rather than a
// top-nav tab, same reasoning as StagingPanel: it's a settings-adjacent tool, not something every
// account should see in their nav bar.
function UsageAnalyticsPanel({ onNavigate }) {
  return (
    <div style={{ marginTop: 32, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Usage Analytics</span>
        <span style={{ fontSize: 11, background: 'var(--border)', color: 'var(--text-muted)', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>Admin only</span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
        See who's using the app, how often, and which pages they visit.
      </p>
      <button
        onClick={() => onNavigate && onNavigate('usage-tracking')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
      >
        <Activity size={15} /> View Usage Analytics
      </button>
    </div>
  );
}

const IS_STAGING = process.env.REACT_APP_IS_STAGING === 'true';

// ── Main component ─────────────────────────────────────────────────────────────
export default function Settings({ user, onNavigate }) {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = async () => {
    try {
      const list = await getUsers();
      setUsers(list);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  useEffect(() => {
    if (user.isAdmin) refresh().finally(() => setLoading(false));
    else setLoading(false);
  }, []);

  const handleCreate = async data => {
    await createUser(data);
    await refresh();
    setModal(null);
  };

  const handleUpdate = async (id, data) => {
    await updateUser(id, data);
    await refresh();
    setModal(null);
  };

  const handleDelete = async id => {
    await deleteUser(id);
    await refresh();
    setDeleteTarget(null);
  };

  return (
    <div className="sm-page">
      <SeasonPanel />
      <ChangePasswordPanel user={user} />
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Notifications</div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>Choose what you get notified about.</p>
        <NotificationsPanel user={user} />
      </div>
      <InstallPanel />
      <ThemePanel />

      {user.isAdmin && <PhysicalTestingPanel user={user} />}

      {user.isAdmin && <PlayerAliasesPanel user={user} />}

      {user.isAdmin && <RPEReminderPanel />}
      {user.isAdmin && <WellnessReminderPanel />}

      {user.isAdmin && <UsageAnalyticsPanel onNavigate={onNavigate} />}

      {user.isAdmin && IS_STAGING && <StagingPanel />}

      {user.isAdmin && (
        <>
          <div className="sm-page__header">
            <div>
              <h2>Account Management</h2>
              <p>Manage user accounts, roles, and tab access permissions.</p>
            </div>
            <button className="sm-btn sm-btn--primary" onClick={() => setModal('create')}>
              <UserPlus size={16} /> Create Account
            </button>
          </div>

          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Tab Access</th>
                  <th>Password</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: '32px' }}>
                      Loading…
                    </td>
                  </tr>
                ) : users.map(u => (
                  <tr key={u.id} className={u.id === user.id ? 'sm-row--self' : ''}>
                    <td>
                      <div className="sm-user-cell">
                        <div className="sm-avatar">{(u.name || u.username)[0].toUpperCase()}</div>
                        <div>
                          <div className="sm-user-name">
                            {u.name}
                            {u.id === user.id && <span className="sm-you-tag">you</span>}
                          </div>
                          <div className="sm-user-uname">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {u.isAdmin || u.role === 'admin'
                        ? <span className="sm-badge sm-badge--admin"><Shield size={11} /> Admin</span>
                        : u.role === 'player'
                          ? <span className="sm-badge sm-badge--player">Player</span>
                          : u.role === 'physio'
                            ? <span className="sm-badge sm-badge--physio">Physio</span>
                            : <span className="sm-badge sm-badge--staff">Staff</span>}
                    </td>
                    <td>
                      <span className="sm-tabs-count">
                        {u.isAdmin ? 'All tabs' : `${u.allowedTabs?.length ?? 0} / ${TAB_OPTIONS.length} tabs`}
                      </span>
                    </td>
                    <td><PasswordCell userId={u.id} /></td>
                    <td>
                      <div className="sm-actions">
                        <button className="sm-icon-btn" onClick={() => setModal(u)} title="Edit account">
                          <Edit2 size={15} />
                        </button>
                        <button
                          className="sm-icon-btn sm-icon-btn--danger"
                          onClick={() => setDeleteTarget(u)}
                          disabled={u.id === user.id}
                          title={u.id === user.id ? "Can't delete your own account" : 'Delete account'}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: '32px' }}>
                      No accounts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {modal === 'create' && (
            <UserModal editing={null} currentUser={user} onSave={handleCreate} onClose={() => setModal(null)} />
          )}

          {modal && modal !== 'create' && (
            <UserModal editing={modal} currentUser={user} onSave={data => handleUpdate(modal.id, data)} onClose={() => setModal(null)} />
          )}

          {deleteTarget && (
            <div className="sm-overlay">
              <div className="sm-confirm">
                <h3>Delete Account</h3>
                <p>Are you sure you want to delete <strong>{deleteTarget.name}</strong>'s account? This cannot be undone.</p>
                <div className="sm-confirm__actions">
                  <button className="sm-btn sm-btn--ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button className="sm-btn sm-btn--danger" onClick={() => handleDelete(deleteTarget.id)}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
